/**
 * Saved machine passwords.
 *
 * What is stored is `sha256(password || salt)`, the same value the desktop
 * client keeps in an address book entry's `hash` field - never the password
 * itself. The peer accepts that hash in place of the password, so a saved
 * machine connects without prompting, and a leaked store does not reveal
 * anything reusable elsewhere.
 *
 * It lives in the user's personal address book on the server, so it follows
 * them between browsers rather than being stuck in one.
 */
import { api } from "./api.js";
import { hashPasswordForStorage, toBase64, fromBase64 } from "rustdesk-protocol/crypto.js";

interface AbPeer {
  id: string;
  hash?: string;
  alias?: string;
  hostname?: string;
  platform?: string;
  username?: string;
}

let personalGuid: string | null = null;

async function guid(): Promise<string> {
  if (personalGuid) return personalGuid;
  const res = await api.post<{ guid: string }>("/api/ab/personal");
  personalGuid = res.guid;
  return personalGuid;
}

/** The stored hash for a machine, or null when none is saved. */
export async function savedHash(peerId: string): Promise<Uint8Array | null> {
  try {
    const ab = await guid();
    const page = await api.post<{ data: AbPeer[] }>(
      `/api/ab/peers?current=1&pageSize=1000&ab=${encodeURIComponent(ab)}`,
    );
    const entry = page.data?.find((p) => p.id === peerId);
    if (!entry?.hash) return null;
    return fromBase64(entry.hash);
  } catch {
    // Not being able to reach the address book must not block connecting.
    return null;
  }
}

/**
 * Saves the reusable hash for a machine, adding it to the address book if it
 * is not there yet.
 */
export async function saveHash(
  peerId: string,
  password: string,
  salt: string,
  info: { hostname?: string; platform?: string; username?: string } = {},
): Promise<void> {
  const hash = toBase64(await hashPasswordForStorage(password, salt));
  const ab = await guid();
  const page = await api.post<{ data: AbPeer[] }>(
    `/api/ab/peers?current=1&pageSize=1000&ab=${encodeURIComponent(ab)}`,
  );
  const exists = page.data?.some((p) => p.id === peerId);

  if (exists) {
    await api.put(`/api/ab/peer/update/${ab}`, { id: peerId, hash });
  } else {
    await api.post(`/api/ab/peer/add/${ab}`, {
      id: peerId,
      hash,
      hostname: info.hostname ?? "",
      platform: info.platform ?? "",
      username: info.username ?? "",
    });
  }
}

export async function forgetHash(peerId: string): Promise<void> {
  const ab = await guid();
  await api.put(`/api/ab/peer/update/${ab}`, { id: peerId, hash: "" });
}
